import { createContext, useContext, type ReactNode } from 'react'
import type { IConnectionService } from './IConnectionService.interface'
import { MockConnectionService }   from './MockConnectionService'
import { HttpConnectionService }   from './HttpConnectionService'

// ── Service resolution ────────────────────────────────────────────────────────
// VITE_CONNECTION_SERVICE controls which service is used at runtime.
// Default: 'mock' for local development.
// Set to 'http' in the Azure DevOps pipeline variable group.

function resolveService(): IConnectionService {
  const key = import.meta.env.VITE_CONNECTION_SERVICE ?? 'mock'
  switch (key) {
    case 'http': return new HttpConnectionService()
    case 'mock':
    default:     return new MockConnectionService()
  }
}

const defaultService = resolveService()

// ── Context ───────────────────────────────────────────────────────────────────

const ConnectionServiceContext = createContext<IConnectionService>(defaultService)

interface ConnectionServiceProviderProps {
  children: ReactNode
  /** Override the service instance — used in tests. */
  service?: IConnectionService
}

export function ConnectionServiceProvider({
  children,
  service: override,
}: ConnectionServiceProviderProps) {
  return (
    <ConnectionServiceContext.Provider value={override ?? defaultService}>
      {children}
    </ConnectionServiceContext.Provider>
  )
}

export function useConnectionService(): IConnectionService {
  return useContext(ConnectionServiceContext)
}
